'use client'

/* eslint-disable max-lines */
import classNames from 'classnames'
import React, { FunctionComponent, useCallback, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import ThemeContext from '@/app/_providers/ThemeProvider/ThemeContext'
import { useSeller, useSitePath } from '@/app/_providers/SellerProvider/SellerProvider'
import { useOurStore } from '@/app/_providers/StoreProvider'
import UrlPromoCodeContext from '@/app/_providers/UrlPromoCodeProvider/UrlPromoCodeContext'
import TheGreatContext from '@/app/_providers/TheGreatProvider/TheGreatContext'
import { useReset } from '@/hooks/useReset'
import {
  cartExists,
  checkoutBlockedAnnouncements,
  getAllLineItemIds,
  getLineItemsByCategory,
  getNonDiscountCartMod,
  handleDeleteEntireOrderHistory,
  mergeLocalAndCartLineItems,
} from '@/utils/cart.utils'
import { getOrderSummarySelectedDate } from '@/utils/dates.utils'
import useCartActions from '@/hooks/useCartActions'
import { getQuantitiesForTier } from '@/utils/components/tickets.utils'
import Announcements from '@/app/_components/Alerts/Announcements/Announcements'
import TXT from '@/messages'
import LineItemsSection from '@/app/_components/LineItemsSection/LineItemsSection'
import SubtotalsList from '@/app/_components/SubtotalsList/SubtotalsList'
import GrandTotalLiveMessage from '@/app/_components/Accessibility/BookedCartLiveMessage/GrandTotalLiveMessage'
import NonDiscountCartMods from '@/app/_components/NonDiscountCartMods/NonDiscountCartMods'
import { PORTAL_SELECTORS } from '@/config/navigation.config'
import { Portal } from '@/app/_components/Portal/Portal'
import lineItemsStyles from '@/styles/templates/LineItems.module.css'
import styles from './OrderSummary.module.css'
import { ButtonContext } from '@/app/_providers/ButtonProvider/ButtonProvider'
import { actionButtonVariations } from '@/app/_components/Button'
import Timer from '@/app/_components/Timer/Timer'
import { QUERY_KEYS } from '@/config/tanstack-querykeys.config'
import { getEventsByIdsAction } from '@/app/_actions/events.actions'
import OrderSummaryHeader from '@/app/_components/OrderSummary/OrderSummaryHeader'
import LoginContext from '@/app/_providers/LoginProvider/LoginContext'
import { OrderSummaryRenderContext } from '@/types/order-summary.types'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import LoadingSpinner from '@/app/_components/LoadingSpinner/LoadingSpinner'

/**
 * Order Summary props
 */
export type Props = {
  /** ClassName */
  className?: string
  /* Callback to delete all tickets from cart */
  onDeleteAllTickets?: () => void
  /* Is this an OrderSummary component that is part of either the footer or the reservation review?
  True prevents its placement in a portal */
  forStickyFooterOrReview?: boolean
  /* Is this the final checkout step? True places action button in order summary on desktop */
  isCheckout?: boolean
}

/**
 * This component Renders the Order Summary
 */
const OrderSummary: FunctionComponent<Props> = ({
  className = '',
  onDeleteAllTickets,
  forStickyFooterOrReview = false,
  isCheckout = false,
}: Props) => {
  const { startTime: selectedStartTime, endTime: selectedEndTime } = useOurStore((state) => state.activeStartTime)
  const activeDate = useOurStore((state) => state.activeDate)
  const localLineItems = useOurStore((state) => state.localLineItems)
  const lineItemsToDelete = useOurStore((state) => state.lineItemsToDelete)
  const updatePartial = useOurStore((state) => state.updatePartial)
  const appliedGiftCards = useOurStore((state) => state.appliedGiftCards)
  // Context
  const { timezone, id: sellerId, isPreview, cityName, exhibitionName } = useSeller()
  const sitePath = useSitePath()
  const admissionType = useOurStore((state) => state.admissionType)
  const { membership, membershipDetails } = useContext(LoginContext)
  const { mobile } = useContext(ThemeContext)
  const { redeemLoading } = useContext(UrlPromoCodeContext)
  const { incomingQueries: { promoCodes: incomingPromoCodes }, isEvents } = useContext(TheGreatContext)
  const { buttonState } = useContext(ButtonContext)
  const { doReset } = useReset()
  const { cart } = useCart()
  const isSticky = !mobile
  const location = !mobile ? 'index' : undefined
  // TODO: get this from somewhere else
  const timerInOrderSummary = !mobile && !isEvents

  const grandTotal = useMemo(() => {
    if (cartExists(cart) && cart.lineItems.length > 0) {
      return cart.grandTotal
    }
    return null
  }, [cart])

  // local items with meta
  const {
    tickets: localTicketsWithMeta,
    addOns: localAddOnsWithMeta,
    events: localEventTicketsWithMeta,
  } = getLineItemsByCategory({ lineItems: localLineItems })

  // cart items with meta
  const {
    tickets: cartTicketsWithMeta,
    addOns: cartAddOnsWithMeta,
    events: cartEventsWithMeta,
  } = getLineItemsByCategory({ lineItems: cart.lineItems })

  // Merge local and cart lineItems for display
  const mergedAdmissionTickets = useMemo(
    () => mergeLocalAndCartLineItems(localTicketsWithMeta, cartTicketsWithMeta, lineItemsToDelete),
    [localTicketsWithMeta, cartTicketsWithMeta, lineItemsToDelete],
  )

  const mergedAddOns = useMemo(
    () => mergeLocalAndCartLineItems(localAddOnsWithMeta, cartAddOnsWithMeta, lineItemsToDelete),
    [localAddOnsWithMeta, cartAddOnsWithMeta, lineItemsToDelete],
  )

  const mergedEventTickets = useMemo(
    () => mergeLocalAndCartLineItems(localEventTicketsWithMeta, cartEventsWithMeta, lineItemsToDelete),
    [localEventTicketsWithMeta, cartEventsWithMeta, lineItemsToDelete],
  )

  const eventIds = [...new Set([
    ...mergedEventTickets.map((item) => item.event.id)]),
  ]

  const { data: orderData, isLoading: orderDataLoading } = useQuery({
    queryKey: [QUERY_KEYS.ORDER_DETAILS, eventIds],
    queryFn: () => getEventsByIdsAction(eventIds),
    enabled: eventIds.length > 0,
  })

  // Shared
  const hasAdmissionTickets = mergedAdmissionTickets.length > 0
  const hasEventTickets = mergedEventTickets.length > 0
  const selectedDate = getOrderSummarySelectedDate(activeDate, timezone)

  const { deleteTickets } = useCartActions({
    sellerId,
    onDeleteAllTickets,
  })

  const deleteCartLineItems = async (tickets: string[]): Promise<void> => {
    // get all ticket ids in cart
    const ticketIds = getAllLineItemIds(cartTicketsWithMeta)
    // get remaining ticket ids after filtering out ticket ids we are removing
    const remainingTicketIds = ticketIds.filter((id) => !tickets.includes(id))

    let ticketsToDelete = tickets

    // exhibition only, ticketIds is only looking at exhibition tickets
    // not event tickets
    // if we are deleting all tickets, delete all add-ons too
    if (remainingTicketIds.length === 0 && ticketIds.length > 0) {
      ticketsToDelete = ticketIds
    }

    await deleteTickets(ticketsToDelete)
  }

  const deleteLocalLineItems = (tierId: string): void => {
    const updatedLocalTickets = localLineItems.filter((el) => el.tier.id !== tierId)
    updatePartial({ localLineItems: updatedLocalTickets })
  }

  const handleDeleteTickets = async (
    { lineItems, tickets }: DeleteArgs,
  ): Promise<void> => {
    if (tickets.length) {
      // write lineItemToDelete to cache
      updatePartial({ lineItemsToDelete: lineItems as Transformed.LineItem[] })
      // there are items in the cart
      await deleteCartLineItems(tickets)
    }
    lineItems.forEach((lineItem) => deleteLocalLineItems(lineItem.tier.id))
  }

  const handleRemoveGiftCard = useCallback((id: string): void => {
    const nextAppliedGiftCards = appliedGiftCards.filter(
      (card: Transformed.GiftCardBalance) => card.number !== id,
    )
    updatePartial({ appliedGiftCards: nextAppliedGiftCards })
  }, [appliedGiftCards, updatePartial])

  const { adjustedQtyForTier } = useMemo(
    () => getQuantitiesForTier({
      localLineItems,
      cartLineItems: cart.lineItems,
      lineItemsToDelete,
    }),
    [localLineItems, cart.lineItems, lineItemsToDelete],
  )
  const hasItems = cart.lineItems.length > 0 || localLineItems.length > 0

  const blockedAnnouncements = useMemo(() => {
    let nextBlockedAnnouncements = checkoutBlockedAnnouncements(cart)
    // if preview - overwrite the message with the message below
    if (isPreview && nextBlockedAnnouncements.length) {
      nextBlockedAnnouncements = [{
        id: 'preview-blocked-announcement',
        message: TXT.checkoutMessages.previewBlocked,
        code: 'preview-blocked',
        priority: 'High',
      }]
    }
    return nextBlockedAnnouncements
  }, [cart, isPreview])
  const nonDiscountCartMods = getNonDiscountCartMod(cart)
  const hasNonDiscountCartMods = nonDiscountCartMods.length > 0

  const actionButton = useMemo(() => actionButtonVariations(
    buttonState,
    {
      className: classNames({ [styles.ActionButton]: !buttonState.digitalWalletPayment }),
      testId: 'order-summary-button',
    },
  ), [buttonState])

  const headerRenderContext: OrderSummaryRenderContext = {
    admissionType,
    cityName,
    isPreview,
    displayMembership: !!membershipDetails?.isValid && !isEvents,
    exhibitionName,
    showCoupons: cartExists(cart),
    sitePath,
    timezone,
  }

  const lineItemsRenderContext: OrderSummaryRenderContext = {
    ...headerRenderContext,
    displayMembership: !!membership,
  }

  const baseOrderSummaryComponent = (
    <>
      <div
        className={classNames(
          styles.OrderSummary,
          className,
          {
            [styles.Sticky]: isSticky,
            [styles.IsEvents]: isEvents,
          },
        )}
        data-testid="order-summary"
        data-location={location}
      >
        <div
          className={styles.OrderContentWrapper}
          title={TXT.a11y.orderSummary}
        >
          {/* not mobile, not events the timer goes in the order summary */}
          {timerInOrderSummary && <Timer handleRestart={onDeleteAllTickets} />}
          <div
            className={classNames(
              styles.OrderDetails,
              { [styles.IsEvents]: isEvents },
            )}
            data-testid="order-summary-details"
          >
            {!isPreview
              && incomingPromoCodes
              && incomingPromoCodes.length > 0
              && !cartExists(cart)
              && (
                <div
                  aria-live="polite"
                  aria-atomic="true"
                  className={styles.UnappliedDiscount}
                >
                  {TXT.alerts.pendingDiscount}
                </div>
              )}

            <OrderSummaryHeader
              entryTime={selectedStartTime}
              endTime={selectedEndTime}
              entryDate={selectedDate}
              isEvent={isEvents}
              renderContext={headerRenderContext}
              displayMembershipPerks={membership?.hasMembership}
            />
            {!redeemLoading && (
              <Announcements show announcements={blockedAnnouncements} />
            )}

            {/* TODO: [TICKT-1763] to prevent layout shift the hr element should show
              if a user has admission tix OR a membership */}
            {hasAdmissionTickets && <hr className={classNames('separator', 'dark')} />}

            {/* Order Summary Title */}
            <div className={styles.HeaderWithRemoveAll}>
              {hasItems && !isEvents && (
                <h2 className={
                  classNames(
                    styles.GeneralTitle,
                    styles.MinorTitle,
                  )
                }
                >
                  {TXT.titles.components.orderSummary}
                </h2>
              )}
              {(cart.lineItems.length > 0 || localLineItems.length > 0) && !isEvents && (
                <button
                  type="button"
                  className={classNames(
                    'btn-unstyled',
                    lineItemsStyles.RemoveLink,
                    lineItemsStyles.RemoveAll,
                  )}
                  onClick={() => handleDeleteEntireOrderHistory(cart, deleteTickets, doReset, updatePartial)}
                  aria-label={TXT.a11y.clearAllTickets}
                  data-testid="remove-all-line-items"
                >
                  {TXT.buttons.clearAll}
                </button>
              )}
            </div>

            {!orderDataLoading
              ? (
                <LineItemsSection
                  cart={cart}
                  events={orderData?.events}
                  venues={orderData?.venues}
                  meta={orderData?.meta}
                  adjustedQtyForTier={adjustedQtyForTier}
                  handleDeleteTickets={handleDeleteTickets}
                  admissionTickets={mergedAdmissionTickets}
                  addOns={mergedAddOns}
                  displayDelete
                  eventTickets={mergedEventTickets}
                  renderContext={lineItemsRenderContext}
                />
              )
              : (<LoadingSpinner />)}

            <div aria-live="polite" aria-atomic="true">
              {hasNonDiscountCartMods && !redeemLoading && (
                <NonDiscountCartMods mods={nonDiscountCartMods} />
              )}
            </div>

            <GrandTotalLiveMessage grandTotal={grandTotal} />
            {/** Mixed cart & local */}
            {(hasAdmissionTickets || hasEventTickets) && (
              <SubtotalsList
                cart={cart}
                giftCardBalance={appliedGiftCards}
                localLineItems={localLineItems}
                lineItemsToDelete={lineItemsToDelete}
                onRemoveGiftCard={handleRemoveGiftCard}
              />
            )}
          </div>
        </div>
        {/** Desktop only */}
        {!mobile && isCheckout && actionButton}
      </div>
    </>
  )
  if (mobile && !forStickyFooterOrReview) {
    return (
      <Portal selector={`#${PORTAL_SELECTORS.mobileOrderSummary}`}>
        {baseOrderSummaryComponent}
      </Portal>
    )
  }
  return baseOrderSummaryComponent
}

export default OrderSummary
